import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmt2, API_BASE } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Heart, Star, Download, FileText, Ban } from "lucide-react";
import { toast } from "sonner";
import ListingCard from "@/components/ListingCard";

export default function TouristDashboard() {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();

  useEffect(() => {
    const tab = params.get("tab");
    if (tab) setTab(tab);
  }, [params]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, w] = await Promise.all([api.get("/bookings"), api.get("/wishlist")]);
      setBookings(b.data); setWishlist(w.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-10" data-testid="tourist-dashboard">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl font-semibold tracking-tight">My account</h1>
        <a href={`${API_BASE}/exports/tourist/bookings.csv`} target="_blank" rel="noreferrer">
          <Button variant="secondary" className="rounded-xl" data-testid="tourist-export-csv"><Download className="h-4 w-4 mr-2" /> Export bookings (CSV)</Button>
        </a>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-6" data-testid="tourist-dashboard-tabs">
        <TabsList className="bg-muted">
          <TabsTrigger value="bookings" data-testid="tab-bookings">My bookings</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="tab-wishlist">Wishlist</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-5">
          {loading ? <p className="text-muted-foreground">Loading…</p> : bookings.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center">
              <p className="font-medium">No bookings yet</p>
              <Link to="/"><Button className="mt-3 rounded-xl">Explore experiences</Button></Link>
            </div>
          ) : (
            <div className="grid gap-3" data-testid="tourist-bookings-list">
              {bookings.map((b) => (
                <BookingRow key={b.booking_id} booking={b} onReviewed={load} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="wishlist" className="mt-5">
          {loading ? <p className="text-muted-foreground">Loading…</p> : wishlist.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="wishlist-empty">
              <Heart className="h-6 w-6 mx-auto text-muted-foreground" />
              <p className="mt-2 font-medium">Your wishlist is empty</p>
              <p className="text-sm text-muted-foreground">Save experiences you love to come back to them.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="wishlist-list">
              {wishlist.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BookingRow({ booking, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const submit = async () => {
    try {
      await api.post("/reviews", { listing_id: booking.listing_id, booking_id: booking.booking_id, rating, title, body });
      toast.success("Review submitted"); setOpen(false); onReviewed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit review"); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this booking? Refund eligibility depends on the vendor's free-cancellation window.")) return;
    setCancelling(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/cancel`);
      toast.success(r.data.eligible_for_refund ? `Cancelled. Eligible for refund of $${r.data.refund_amount}.` : "Cancelled. Outside free-cancellation window \u2014 no refund.");
      onReviewed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    setCancelling(false);
  };
  const isActive = !["cancelled", "refunded"].includes(booking.status);
  return (
    <div className="rounded-2xl bg-card border border-border p-4 flex gap-4" data-testid="booking-row">
      {booking.image && <img src={booking.image} alt="" className="h-20 w-28 object-cover rounded-xl" />}
      <div className="flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold">{booking.title}</div>
            <div className="text-xs text-muted-foreground">ID: {booking.booking_id}</div>
            <div className="text-sm mt-1">Date: <span className="font-medium">{booking.date}</span> \u00b7 Guests: <span className="font-medium">{booking.guests}</span> \u00b7 <span className={`capitalize ${booking.status === "cancelled" ? "text-rose-600" : booking.status === "refunded" ? "text-emerald-700" : booking.status === "paid" ? "text-emerald-700" : ""}`}>{booking.status}</span></div>
          </div>
          <div className="text-right">
            <div className="font-semibold">{fmt2(booking.line_final)}</div>
            {booking.refund_amount > 0 && <div className="text-xs text-emerald-700">Refunded {fmt2(booking.refund_amount)}</div>}
          </div>
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <Link to={`/listings/${booking.listing_id}`}><Button variant="ghost" className="rounded-xl">View listing</Button></Link>
          <a href={`${API_BASE}/exports/booking/${booking.booking_id}.pdf`} target="_blank" rel="noreferrer">
            <Button variant="ghost" className="rounded-xl" data-testid="booking-download-invoice"><FileText className="h-4 w-4 mr-2" /> Invoice (PDF)</Button>
          </a>
          {isActive && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-xl" data-testid="booking-write-review-button">Write review</Button>
              </DialogTrigger>
              <DialogContent className="bg-popover">
                <DialogHeader><DialogTitle>Review: {booking.title}</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <div>
                    <label className="text-sm">Your rating</label>
                    <div className="mt-1 flex gap-1">
                      {[1,2,3,4,5].map((n) => (
                        <button key={n} type="button" onClick={() => setRating(n)} className="p-1" data-testid={`review-star-${n}`}>
                          <Star className={`h-6 w-6 ${n <= rating ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="text-sm">Title
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="review-title-input" />
                  </label>
                  <label className="text-sm">Review
                    <Textarea required rows={4} value={body} onChange={(e) => setBody(e.target.value)} className="mt-1" data-testid="review-body-input" />
                  </label>
                </div>
                <DialogFooter>
                  <Button onClick={submit} disabled={!body} className="rounded-xl" data-testid="review-submit-button">Submit review</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isActive && (
            <Button onClick={cancel} variant="ghost" className="rounded-xl text-rose-600 hover:text-rose-700" disabled={cancelling} data-testid="booking-cancel-button"><Ban className="h-4 w-4 mr-2" /> {cancelling ? "Cancelling..." : "Cancel"}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
